/* eslint-disable new-cap */
/* eslint-disable max-statements */
import { BlueprintInstanceType } from '../shared.enums';
import { getCutoutCenterCfn } from './functions/get-cutout-center.cfn';
import { Island } from '../islands/island.class';
import { Item } from '../item/item.class';
import { Panel } from '../panels/panel.class';
import { panelItemData } from './panel-item.data';
import { PanelItemGroup } from '../panel-item-groups/panel-item-group.class';
import { Publish } from '../../decorators';
import { ReferenceMap } from '../shared.types';
import { setBit } from '../../helpers';
import { Size } from '../../types';

import { alignToBottomCfn, getCornersCfn, getCutoutSizeCfn, getDefaultBoundToEdgeCfn, getDefaultPlacementCenterYCfn, getItemCenterCfn, getNextItemCfn, getOffsetsCfn, getPanelSleeveCfn, getPreviousItemCfn, getSizeCfn, moveCfn, positionHorizontallyCfn } from './functions';
import { BoundToEdge, Components } from '../../component-classes';
import { CoordinatePoint3, Vertex3 } from '../../spatial-classes';
import { DimensionOffsets, PanelItemCorners, PanelItemDataIn, PanelItemDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export type PanelItemConstructorParams = KitchenDesignOptions<PanelItemDataIn> & {
	sku?: string;
};

/** The horizontal (left/right) edge bits — where the column sits, not how the item mounts. */
const HORIZONTAL_BINDING = BoundToEdge.Left | BoundToEdge.Right;

export class PanelItem extends KitchenDesignBase<PanelItemDataIn> {
	#boundToEdge: number;
	#dimensionOffsets: DimensionOffsets;
	#fullHeight: boolean;
	#ruleManaged: boolean;
	#item: Item;
	#sleeve: string | null;
	#vendor: string;
	#description: string | null;
	#center: Vertex3;
	#instanceRotation: Vertex3;
	#movableAxis: { x: boolean, y: boolean, z: boolean };
	#island: Island;
	#panel: Panel;
	#panelItemGroup: PanelItemGroup;

	readonly offset: Vertex3;

	public sidePadding: number;

	constructor (options: PanelItemConstructorParams = {}) {
		super(BlueprintInstanceType.PanelItem, options);

		const { sku = null, dataIn = null } = options;
		const data = panelItemData(dataIn);

		this.#movableAxis = { x: false, y: true, z: false };

		this._id = data.id;
		this.#dimensionOffsets = data.dimensionOffsets as DimensionOffsets;
		this.#item = new Item({ sku, dataIn: data.item, parent: this });

		if (dataIn && typeof dataIn.dimensionOffsets === 'undefined') {
			const { label, line } = this.#item.component.dimensions ?? {};
			if (label) {
				this.#dimensionOffsets.hLabel = label.x;
				this.#dimensionOffsets.vLabel = label.y;
				this.#dimensionOffsets.zLabel = label.z;
			}

			if (line) {
				this.#dimensionOffsets.hLine = line.x;
				this.#dimensionOffsets.vLine = line.y;
				this.#dimensionOffsets.zLine = line.z;
			}
		}

		// this.#vendor = data.vendor;
		this.#center = new Vertex3({ dataIn: data.center });
		this.#instanceRotation = new Vertex3({ dataIn: data.instanceRotation ?? { x: 0, y: 0, z: 0 } });

		this.#boundToEdge = data.boundToEdge > 0 ? data.boundToEdge : getDefaultBoundToEdgeCfn(this.#item);

		this.#fullHeight = data.fullHeight ?? false;

		// ruleManaged: saved value > legacy default (true) > component setting
		const explicitRuleManaged = dataIn && typeof dataIn.ruleManaged === 'boolean';
		if (explicitRuleManaged) {
			this.#ruleManaged = dataIn.ruleManaged;
		} else if (dataIn?.id) {
			// legacy saved data without ruleManaged field — default managed
			this.#ruleManaged = true;
		} else {
			// new item (from SKU or direct constructor) — use component setting
			this.#ruleManaged = this.#item.component.ruleManaged ?? true;
		}

		// this.#description = data.description;

		this.offset = new Vertex3({ dataIn: data.offset });
		this.#island = this.refMap?.island ?? null;
		this.#panelItemGroup = this.refMap?.panelItemGroup ?? null;
		this.#panel = this.refMap?.panel ?? null;
		this.sidePadding = data.sidePadding;

		this.#sleeve = data.sleeve;

		if (options.parent && options.parent.initialized) positionHorizontallyCfn(this);

		// for new bottom-only items, snap to panel bottom once at construction time
		if (this.boundBottom && options.parent && options.parent.initialized && this.#panel && data.center.y === 0) {
			const halfPanelHeight = this.#panel.size.height / 2;
			this.#center.y = halfPanelHeight - (this.#item.component.size.height / 2);
		}

		// only a brand-new item takes the catalog defaultPlacement -- a saved item keeps its stored center (same new-vs-saved test as ruleManaged above)
		if (!dataIn?.id && options.parent && options.parent.initialized && this.#panel) {
			const defaultCenterY = getDefaultPlacementCenterYCfn(this.#item, this.#panel.size.height);
			if (defaultCenterY !== null) this.#center.y = defaultCenterY;
		}

		this.setInitialized();
	}

	get item (): Item { return this.#item }

	@Publish('panel_item_dimension_updates', 'updated')
	set dimensionOffsets (dimensionOffsets: Partial<DimensionOffsets>) { this.#dimensionOffsets = { ...this.#dimensionOffsets, ...dimensionOffsets }; }
	get dimensionOffsets (): DimensionOffsets { return this.#dimensionOffsets }

	/**
	 * panel item center represents the position relative to the parent panel item group
	 * @returns Vertex3 representing the center position relative to the parent panel item group
	 */
	get center (): Vertex3 { return getItemCenterCfn(this as PanelItem, this.#panelItemGroup, this.#center as Vertex3, this.context); }

	/**
	 * panel item origin represents the world space position of the panel item
	 * origin = parentPanelItemGroup.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const origin = new Vertex3();
		if (this.#panelItemGroup) {
			origin.moveTo(this.#panelItemGroup.origin);
			origin.add(this.center);
		}
		return origin;
	}

	get cutout (): Size { return getCutoutSizeCfn(this as PanelItem); }
	get offsets (): { item: Vertex3, cutout: Vertex3 } { return getOffsetsCfn(this as PanelItem); }
	get size (): Size { return getSizeCfn(this as PanelItem, this.#panel as Panel); }

	@Publish('panel_item_description', 'updated')
	set description (description: string) { this.#description = description; }
	get description (): string { return this.#description === null ? (this.#item === null ? 'Sku Description Not Set' : this.#item.component.description) : this.#description; }

	@Publish('panel_item_bound_2_edge', 'updated')
	set boundToEdge (bound2Edge: number) { this.#boundToEdge = bound2Edge; }
	get boundToEdge (): number { return this.#boundToEdge }

	@Publish('panel_item_bound_left', 'updated')
	set boundLeft (bind: boolean) { this.#boundToEdge = setBit(this.#boundToEdge, BoundToEdge.Left, bind); }
	get boundLeft (): boolean { return (this.#boundToEdge & BoundToEdge.Left) !== 0; }

	@Publish('panel_item_bound_right', 'updated')
	set boundRight (bind: boolean) { this.#boundToEdge = setBit(this.#boundToEdge, BoundToEdge.Right, bind); }
	get boundRight (): boolean { return (this.#boundToEdge & BoundToEdge.Right) !== 0; }

	@Publish('panel_item_bound_top', 'updated')
	set boundTop (bind: boolean) { this.#boundToEdge = setBit(this.#boundToEdge, BoundToEdge.Top, bind); }
	get boundTop (): boolean { return (this.#boundToEdge & BoundToEdge.Top) !== 0; }

	@Publish('panel_item_bound_bottom', 'updated')
	set boundBottom (bind: boolean) { this.#boundToEdge = setBit(this.#boundToEdge, BoundToEdge.Bottom, bind); }
	get boundBottom (): boolean { return (this.#boundToEdge & BoundToEdge.Bottom) !== 0; }

	get bound2Counter (): boolean { return this.isDropIn || this.boundTop || (!this.boundBottom && (this.#panel?.size.height - this.item.component.cutout.height < 2.75)); }

	get earSize (): number { return (this.size.width - this.cutout.width) / 2; }

	@Publish('panel_item_full_height', 'updated')
	set fullHeight (fullHeight: boolean) { this.#fullHeight = fullHeight; }
	get fullHeight (): boolean { return this.#fullHeight; }

	set ruleManaged (ruleManaged: boolean) { this.#ruleManaged = ruleManaged; }
	get ruleManaged (): boolean { return this.#ruleManaged; }

	// get showHint (): boolean { return this.item.placement === Components.PLACEMENT_TOP_ONLY && !isCountertop; }
	get isStandAlone (): boolean { return this.item.component.application === Components.APPLICATION_STAND_ALONE; }
	get isDropIn ():boolean { return this.item.component.placement === Components.PLACEMENT_TOP_ONLY && this.item.component.distanceBelowCountertop > 0; }
	get isFullHeightAppliance (): boolean {
		// For bottom-only items (boundBottom without boundTop), respect the explicit fullHeight toggle
		// rather than auto-detecting based on size proximity to panel height
		if (this.boundBottom && !this.boundTop && !this.isDropIn) return this.#fullHeight;
		return (this.bound2Counter && this.boundBottom) || this.#fullHeight;
	}
	get modelOffset (): Vertex3 { return this.item.component.modelOffset; }

	/**
	 * Per-instance rotation offset applied on top of the catalog modelRotation
	 * @returns Vertex3 representing the per-instance rotation in degrees
	 */
	@Publish('panel_item_rotation', 'updated')
	set instanceRotation (value: Vertex3) { this.#instanceRotation = value; }
	get instanceRotation (): Vertex3 { return this.#instanceRotation; }

	/**
	 * Combined rotation: catalog modelRotation + per-instance instanceRotation
	 * @returns Vertex3 representing the total effective rotation in degrees
	 */
	get effectiveRotation (): Vertex3 {
		const { x: cx, y: cy, z: cz } = this.item.component.modelRotation;
		const { x: ix, y: iy, z: iz } = this.#instanceRotation;
		return new Vertex3({ dataIn: { x: cx + ix, y: cy + iy, z: cz + iz } });
	}

	get sleeve (): string | null { return getPanelSleeveCfn(this as PanelItem); }

	get hasLandingZone (): boolean {
		const c = this.#item.component;
		return c.cutout.width !== c.size.width || c.cutout.height !== c.size.height;
	}
	get hasEars (): boolean {
		const c = this.#item.component;
		return c.cutout.width < c.size.width && c.placement === Components.PLACEMENT_FRONT_AND_TOP;
	}

	/**
	 * Moves the item by the delta provided
	 * @param axisDelta deltas for x, y and z move
	 */
	@Publish('panel_item', 'moved')
	public move (axisDelta: Partial<CoordinatePoint3<number>>): void { moveCfn(axisDelta, this as PanelItem); }

	/**
	 * Aligns this panelItem to the bottom-most panelItem on the panel
	 */
	public alignToBottom (): void { alignToBottomCfn(this as PanelItem) }

	/**
	 * returns the center of the panelItem
	 * @param cutout true to use cutout, otherwise overall size
	 * @returns Vertex3
	 */
	public getCenter (cutout = false): Vertex3 {
		// if cutout is true and the item placement is front and top, then return the cutout center
		if (cutout && !(this.item.placement === Components.PLACEMENT_TOP_ONLY && this.item.distanceBelowCountertop === null)) {
			const center = getCutoutCenterCfn(this as PanelItem, this.#panelItemGroup, this.context);
			return center;
		}

		// all other cutouts = item center
		return this.center.clone();
	}
	/**
	 * returns panelItem corner points for each view
	 * @param inside true to use cutout, otherwise overall size
	 * @param includeOverhangs include overhangs when calculating corners
	 * @returns PanelItemCorners type
	 */
	public getCorners (inside = false, includeOverhangs = false, applyInstanceRotation = false): PanelItemCorners { return getCornersCfn(this as PanelItem, { inside, includeOverhangs, applyInstanceRotation }); }

	/**
	 * finds the next PanelItem in the PanelItemGroup where y > current PaneItem y
	 * @returns PanelItem
	 */
	public getNextItem (includePlacements = null): PanelItem { return getNextItemCfn(this as PanelItem, includePlacements); }

	/**
	 * finds the next PanelItem in the PanelItemGroup where y < current PaneItem y
	 * @returns PanelItem
	 */
	public getPreviousItem (includePlacements = null): PanelItem { return getPreviousItemCfn(this as PanelItem, includePlacements); }

	/**
	 * @param id id to find child reference for
	 * @returns ReferenceMap
	 */
	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['item']);
	}

	/**
	 * uses item sku to refresh item values, also resets description
	 */
	public refresh (): void { this.swapItem(this.item.component.sku); }

	/**
	 * Replaces the current item with a new sku, updates all item fields and resets PanelItem descirption
	 * @param sku string - sku of the item to use in the replacement
	 */
	@Publish('panel_item_appliance', 'replaced')
	public swapItem (sku: string): void {
		const skuChanged = this.#item?.component?.sku !== sku;
		this.#item = new Item({ sku, parent: this });
		this.#description = null;
		// A different component mounts differently, so the new one must be
		// positioned exactly like a freshly added item of it — none of the old
		// component's derived placement may carry over. A same-SKU refresh()
		// changes nothing.
		if (skuChanged) {
			// Vertical binding comes from placement. Kept, it pinned a front-only
			// appliance swapped in for a top-bound one (a pull-out cooler replacing
			// a drop-in refreshment center) to the top edge, where
			// centerItemsVertically never moves bound items. The left/right bits
			// say where the column sits, so they carry over.
			this.#boundToEdge = (this.#boundToEdge & HORIZONTAL_BINDING) | getDefaultBoundToEdgeCfn(this.#item);

			// Depth: getItemCenterCfn writes a derived z into #center for items it
			// can place from their component (front-of-face / front-only), and
			// puts a top-bound item at the back of the panel ONLY while the stored
			// z is still 0. Kept, the old appliance's derived depth made a pizza
			// oven swapped in for a sink sit at the sink's depth. Depth is never
			// set by hand (movableAxis.z is false), so resetting it to the
			// fresh-item default loses nothing. x (the column) and y (re-derived
			// or re-distributed on the next clean) stay.
			this.#center.z = 0;
		}
	}

	/**
	 * Re-binds this panel item to its appliance's catalog row for `fuelchoice`, preserving the
	 * instance state that lives on the Item (exclude, skuColor, any per-item fuelchoice). Placement
	 * lives on the PanelItem, so it is untouched. A no-op for an override item (its component is a
	 * private snapshot, not a catalog row), for an appliance with no fuel variants, and when the
	 * item is already on the right row.
	 * @param fuelchoice the fuel to bind to
	 * @returns true when the bound row actually changed
	 */
	public rebindFuelVariant (fuelchoice: string): boolean {
		if (this.#item.override) return false;

		const variant = this.context.components.resolveFuelVariant(this.#item.component, fuelchoice);
		if (!variant || variant.id === this.#item.component?.id) return false;

		this.#item = new Item({ dataIn: { ...this.#item.serialize(), id: variant.id }, parent: this });
		this.#description = null;
		return true;
	}

	/**
	 * Runs fn with this panel item's Item temporarily replaced by a catalog Item for the
	 * given sku, restoring the exact original Item before returning. Lets a consumer (the
	 * 3D pipeline) read a placeholder appliance's full geometry — center, cutout, eared
	 * corners, sleeve offset — through the real cfns instead of re-deriving it. The vertical
	 * edge-binding (Top/Bottom) is also stood in from the placeholder's placement so it seats
	 * exactly like a real instance; the custom item's horizontal (Left/Right) binding is kept.
	 * Synchronous only; publishes nothing.
	 * @param sku sku of the placeholder catalog item to stand in
	 * @param fn reader invoked with this panelItem while the placeholder item is active
	 */
	public withPlaceholderItem<T> (sku: string, fn: (panelItem: PanelItem) => T): T {
		const originalItem = this.#item;
		const originalBound = this.#boundToEdge;
		this.#item = new Item({ sku, parent: this });

		this.#boundToEdge = (originalBound & (BoundToEdge.Left | BoundToEdge.Right)) | getDefaultBoundToEdgeCfn(this.#item);

		try {
			return fn(this as PanelItem);
		} finally {
			this.#item = originalItem;
			this.#boundToEdge = originalBound;
		}
	}

	public serialize (): PanelItemDataOut {
		return {
			id: this._id,
			// description: this.#description,
			boundToEdge: this.#boundToEdge,
			fullHeight: this.#fullHeight,
			ruleManaged: this.#ruleManaged,
			dimensionOffsets: this.#dimensionOffsets,
			item: this.#item.serialize(),
			sleeve: this.#sleeve,
			// vendor: this.#vendor,
			sidePadding: this.sidePadding,
			center: this.#center.serialize(),
			offset: this.offset.serialize(),
			instanceRotation: this.#instanceRotation.serialize()
		};
	}
}
